import type { FC } from 'react'
import { isNetworkSupported } from '@g4g/wallet-modal/util/network'
import CheckoutPaymentTokenPicker from './tokens/CheckoutPaymentTokenPicker'
import CheckoutPaymentSubmit from './submit/CheckoutPaymentSubmit'
import { useCheckout } from '../../CheckoutProvider'
import { useShoppingCartTotals } from '../../../shopping-cart/hooks/useShoppingCartTotals'
import type { Web3ContextProps } from '@g4g/wallet-modal/components/Web3ContextProvider'
import UnsupportedNetworkKorag from '../../../shared/wallet/UnsupportedNetworkKorag'
import CheckoutPaymentRecipient from './CheckoutPaymentRecipient'
import useRecipientUsers from '../../../recipient/hooks/useRecipientUsers'
import ShoppingCartSubtotal from '../../../shopping-cart/cart-page/ShoppingCartSubtotal'
import type { ProductPriceTotals } from '../../../shopping-cart/provider/types'
import type { ERC20Names } from '@g4g/wallet-modal/types'

const CheckoutTotal: FC<{ paymentOption: ERC20Names; price: ProductPriceTotals }> = ({
  paymentOption,
  price: { totalPrps, totalDubi, discountSavingsDubi, discountSavingsPrps, originalTotalPrps },
}) => (
  <ShoppingCartSubtotal
    tokenSymbol={paymentOption === 'Purpose' ? 'PRPS' : 'DUBI'}
    total={paymentOption === 'Purpose' ? totalPrps : totalDubi}
    original={originalTotalPrps}
    savings={paymentOption === 'Purpose' ? discountSavingsPrps : discountSavingsDubi}
  />
)

const CheckoutPaymentConnected: FC<Required<Pick<Web3ContextProps, 'account' | 'chainId'>>> = ({
  chainId,
  account,
}) => {
  const { paymentOption, error: checkoutError } = useCheckout()
  const { total } = useShoppingCartTotals()
  const { currentRecipient, loading: loadingRecipient } = useRecipientUsers()

  return (
    <>
      {isNetworkSupported(chainId) ? (
        <div className="flex flex-col justify-between w-full h-full">
          {currentRecipient && (
            <div className="lg:order-2">
              <CheckoutPaymentSubmit
                account={account}
                paymentOption={paymentOption}
                loading={loadingRecipient}
                web3CheckoutError={checkoutError ? new Error(checkoutError) : undefined}
              />
            </div>
          )}
          <div className="py-4 lg:order-1">
            <div>
              <div className="mb-2 text-sm text-white/50">Payment Option</div>
              <CheckoutPaymentTokenPicker total={total} option={paymentOption} />
            </div>
            {total?.price.totals && (
              <div className="mt-4">
                <CheckoutTotal price={total.price.totals} paymentOption={paymentOption} />
              </div>
            )}
          </div>
          <CheckoutPaymentRecipient />
        </div>
      ) : (
        <UnsupportedNetworkKorag>
          Switch to the Polygon Network to complete the purchase
        </UnsupportedNetworkKorag>
      )}
    </>
  )
}

export default CheckoutPaymentConnected
